/**
 * Processing Activities (Record of Processing Activities, GDPR Article 30) — PDF export.
 *
 * The layout engine lives in `pdfDoc.ts` — see that file for the pagination rationale. This
 * module is only the ROPA-specific composition on top of it, mirroring `aiUseCasePdf.ts`'s
 * shape closely since both registers share the `DataField` type and much of the same GDPR
 * vocabulary (`constants/privacy.ts` re-exports rather than forks it).
 *
 * Unlike the AI Use Case Register, none of this register's eight sections argue for one
 * another the way AI Act classification, oversight, vendors and monitoring do — each maps
 * onto its own Article 30(1) sub-clause fairly independently — so there is no equivalent of
 * `renderClassification`'s bundling. Every section renders inline in `renderActivity`,
 * matching how the AI file's own simpler sections (business case, outcomes, governance) are
 * written directly rather than delegated to a sub-function.
 */
import type { ProcessingActivity, ActivityStatus, DpiaVerdict } from '../types/models';
import {
  PROCESSOR_ROLE_LABELS,
  ACTIVITY_STATUS_LABELS,
  ACTIVITY_STATUS_TONE,
  DATA_SUBJECT_CATEGORY_LABELS,
  SUBJECT_VOLUME_LABELS,
  RECIPIENT_CATEGORY_LABELS,
  TRANSFER_MECHANISM_LABELS,
  TRANSFER_MECHANISM_TONE,
  DPIA_VERDICT_LABELS,
  DPIA_VERDICT_TONE,
  DATA_CATEGORY_LABELS,
  SENSITIVITY_LABELS,
  LEGAL_BASIS_OPTIONS,
  hasSpecialCategoryData,
  dataFieldRiskCell,
} from '../constants/privacy';
import { Doc, T, htmlToParagraphs, stripHtml, identityField, type NameLookup, type PdfBuildResult } from './pdfDoc';

const FOOTER_LABEL = 'Mercell Nimbus — Record of Processing Activities';

const SENSITIVITY_TONE: Record<string, string> = {
  public: 'success', internal: 'info', confidential: 'warning', restricted: 'danger',
};

// ─── Section renderers ────────────────────────────────────────────────────────

export interface PdfSections {
  /** Name, role, joint controllers, owner, department, description. */
  identity: boolean;
  /** Purposes, lawful basis, the Article 6(1)(f) balancing test, the Article 9(2) condition. */
  purpose: boolean;
  /** Categories of data subject, approximate volume, vulnerable-group flag. */
  dataSubjects: boolean;
  /** The data field inventory — same table shape as the AI Use Case Register's. */
  dataFields: boolean;
  recipients: boolean;
  transfers: boolean;
  retention: boolean;
  security: boolean;
  governance: boolean;
}

export const ALL_SECTIONS: PdfSections = {
  identity: true, purpose: true, dataSubjects: true, dataFields: true,
  recipients: true, transfers: true, retention: true, security: true, governance: true,
};

const legalBasisLabel = (value: string): string =>
  LEGAL_BASIS_OPTIONS.find(o => o.value === value)?.label || value || '';

/** Id eyebrow, title, then status, DPIA verdict and (conditional) special-category chips —
 *  exactly the chips the detail page's own header shows, in the same order. */
function activityHeading(doc: Doc, activity: ProcessingActivity): void {
  const status = (activity.status || 'draft') as ActivityStatus;
  const verdict = (activity.dpiaVerdict || '') as DpiaVerdict;
  const chips: Array<{ text: string; tone: string }> = [
    { text: ACTIVITY_STATUS_LABELS[status] ?? status, tone: ACTIVITY_STATUS_TONE[status] ?? 'neutral' },
    { text: DPIA_VERDICT_LABELS[verdict] ?? 'Not screened', tone: DPIA_VERDICT_TONE[verdict] ?? 'neutral' },
  ];
  if (hasSpecialCategoryData(activity.dataFields ?? [])) {
    chips.push({ text: 'Special category data', tone: 'danger' });
  }
  doc.recordHeading(activity.activityId, activity.name || activity.activityId, chips);
}

function renderActivity(doc: Doc, activity: ProcessingActivity, sections: PdfSections, lookupName?: NameLookup): void {
  activityHeading(doc, activity);

  if (sections.identity) {
    doc.sectionHeading('Identity and Role');
    doc.metaRow('Our role', PROCESSOR_ROLE_LABELS[activity.role] ?? activity.role);
    // Only meaningful for a joint controller — printing it regardless would show a "Joint
    // controllers: Not recorded" row on every sole-controller record, which is not a gap,
    // it is the field being inapplicable.
    if (activity.role === 'joint-controller') {
      doc.metaRow('Joint controllers', activity.jointControllers || '—');
    }
    doc.space(1);
    identityField(doc, 'Owner', activity.ownerId, lookupName);
    identityField(doc, 'Department', activity.departmentId, lookupName);
    doc.space(1.5);
    const description = htmlToParagraphs(activity.description);
    if (description.length) doc.fieldParagraphs('Description', description);
    else doc.field('Description', '');
  }

  if (sections.purpose) {
    doc.sectionHeading('Purpose and Lawful Basis');
    const purposes = htmlToParagraphs(activity.purposes);
    if (purposes.length) doc.fieldParagraphs('Purposes of the processing', purposes);
    else doc.field('Purposes of the processing', '');
    doc.field('Lawful basis (Article 6)', legalBasisLabel(activity.legalBasis));

    // Both of these are conditional on the detail page too — legitimate-interests-only and
    // special-category-only respectively — so an export of a record that never triggered
    // either prints neither, rather than two "Not recorded" rows for fields that were never
    // asked for.
    if (activity.legalBasis === 'legitimate-interests') {
      const lia = htmlToParagraphs(activity.legitimateInterestsAssessment);
      if (lia.length) doc.fieldParagraphs('Legitimate interests assessment (Article 6(1)(f))', lia);
      else doc.field('Legitimate interests assessment (Article 6(1)(f))', '');
    }
    if (hasSpecialCategoryData(activity.dataFields ?? [])) {
      const condition = htmlToParagraphs(activity.specialCategoryCondition);
      if (condition.length) doc.fieldParagraphs('Article 9(2) condition', condition);
      else doc.field('Article 9(2) condition', '');
    }
  }

  if (sections.dataSubjects) {
    doc.sectionHeading('Data Subjects');
    const categories = (activity.dataSubjectCategories ?? [])
      .map(c => DATA_SUBJECT_CATEGORY_LABELS[c] ?? c);
    doc.field('Categories of data subject', categories.join(', '));
    doc.metaRow('Approximate number of data subjects', SUBJECT_VOLUME_LABELS[activity.dataSubjectVolume] ?? 'Not yet estimated');
    doc.metaRow('Vulnerable data subjects', activity.isVulnerableGroup ? 'Yes' : 'No');
  }

  if (sections.dataFields) {
    const fields = activity.dataFields ?? [];
    doc.sectionHeading('Categories of Personal Data');
    if (fields.length) {
      // Same six columns as the AI Use Case Register's data-field table — same DataField
      // type, same dataFieldRiskCell, same widths. Diverging here for no reason would be
      // the two registers drifting apart on a table they are otherwise identical for.
      doc.table(
        ['Data field', 'Category', 'Personal', 'Sensitivity', 'Purpose', 'Source'],
        [36, 26, 18, 22, 42, 34],
        fields.map(f => [
          f.name || '—',
          DATA_CATEGORY_LABELS[f.category] ?? f.category,
          dataFieldRiskCell(f),
          { text: SENSITIVITY_LABELS[f.sensitivity] ?? f.sensitivity, tone: SENSITIVITY_TONE[f.sensitivity] ?? 'neutral' },
          f.processingPurpose || '—',
          f.dataSource || '—',
        ]),
      );
    } else {
      doc.paragraph('No data fields recorded.', T.small, { gap: 2 });
    }
  }

  if (sections.recipients) {
    doc.sectionHeading('Recipients');
    const categories = (activity.recipientCategories ?? [])
      .map(c => RECIPIENT_CATEGORY_LABELS[c] ?? c);
    doc.field('Categories of recipient', categories.join(', '));
    const processors = activity.processors ?? [];
    if (processors.length) doc.fieldParagraphs('Processors', processors);
    else doc.field('Processors', '');
  }

  if (sections.transfers) {
    const transfers = activity.transfers ?? [];
    doc.sectionHeading('Transfers Outside the EEA');
    if (transfers.length) {
      doc.table(
        ['Country', 'Recipient', 'Mechanism', 'Safeguards'],
        [38, 48, 48, 44],
        transfers.map(t => [
          t.country || '—',
          t.recipient || '—',
          { text: TRANSFER_MECHANISM_LABELS[t.mechanism] ?? t.mechanism, tone: TRANSFER_MECHANISM_TONE[t.mechanism] ?? 'neutral' },
          t.safeguards || '—',
        ]),
      );
    } else {
      doc.paragraph('No transfers outside the EEA recorded.', T.small, { gap: 2 });
    }
  }

  if (sections.retention) {
    const retention = activity.retention ?? [];
    doc.sectionHeading('Retention');
    if (retention.length) {
      doc.table(
        ['Data category', 'Retention period', 'What starts the clock'],
        [46, 40, 92],
        retention.map(r => [
          DATA_CATEGORY_LABELS[r.dataCategory] ?? r.dataCategory,
          r.period || '—',
          r.trigger || '—',
        ]),
      );
    } else {
      doc.paragraph('No retention rules recorded.', T.small, { gap: 2 });
    }
  }

  if (sections.security) {
    doc.sectionHeading('Security Measures');
    const measures = htmlToParagraphs(activity.securityMeasures);
    if (measures.length) doc.paragraphs(measures);
    else doc.paragraph('Not provided.', T.small, { gap: 2 });
  }

  if (sections.governance) {
    doc.sectionHeading('Record');
    doc.metaRow('Status', ACTIVITY_STATUS_LABELS[(activity.status || 'draft') as ActivityStatus] ?? activity.status);
    doc.metaRow('Created', activity.createdAt ? new Date(activity.createdAt).toLocaleString('en-GB') : '—');
    doc.metaRow('Last updated', activity.updatedAt ? new Date(activity.updatedAt).toLocaleString('en-GB') : '—');
  }
}

// ─── Public entry point ───────────────────────────────────────────────────────

export type { PdfBuildResult };

/** Build the document without triggering a download. Exported so the pagination
 *  guarantee can be verified headlessly. */
export function buildProcessingActivitiesPdf(
  activities: ProcessingActivity[],
  sections: PdfSections = ALL_SECTIONS,
  /** `names` maps a stored id to a display name — personIds (ownerId) and departmentIds
   *  together, since the two id spaces do not collide. Anything unresolved is printed as an
   *  id under a label that says so, never as if it were a name. */
  opts: { onePerPage?: boolean; now?: Date; names?: Record<string, string> } = {},
): PdfBuildResult {
  const lookupName: NameLookup | undefined = opts.names
    ? (id: string) => opts.names?.[id]
    : undefined;
  const generatedAt = (opts.now ?? new Date()).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const doc = new Doc(generatedAt, FOOTER_LABEL);
  const plural = activities.length === 1 ? 'activity' : 'activities';
  doc.coverTitle('Record of Processing Activities', `${activities.length} processing ${plural} · generated ${generatedAt}`);

  activities.forEach((activity, i) => {
    if (i > 0) {
      if (opts.onePerPage !== false) doc.pageBreak();
      else doc.space(6);
    }
    renderActivity(doc, activity, sections, lookupName);
  });

  const { pdf, pages, maxYByPage, overflows } = doc.finish();
  const filename = activities.length === 1
    ? `${activities[0].activityId}-processing-activity.pdf`
    : `processing-activities-${activities.length}-records.pdf`;

  return { pages, maxYByPage, overflows, filename, save: () => pdf.save(filename) };
}

/** Build and download. */
export function exportProcessingActivitiesPdf(
  activities: ProcessingActivity[],
  sections: PdfSections = ALL_SECTIONS,
  opts: { onePerPage?: boolean; names?: Record<string, string> } = {},
): void {
  buildProcessingActivitiesPdf(activities, sections, opts).save();
}

export { stripHtml };
